/**
 * Matrices — logique pure (Boîte à outils, Tle maths expertes → prépa).
 *
 * Rationnels EXACTS en BigInt : les puissances d'une matrice de transition
 * (dixièmes) ou d'une matrice entière grandissent vite, on ne veut ni
 * dépassement ni arrondi caché. À l'affichage : entier, décimal s'il tombe
 * juste (au plus 6 décimales), fraction si elle reste lisible (6 chiffres au
 * plus), sinon valeur approchée signalée (« ≈ »).
 */

export interface Q {
  n: bigint;
  /** toujours > 0, fraction réduite */
  d: bigint;
}

const abs = (x: bigint) => (x < 0n ? -x : x);
const NNBSP = String.fromCharCode(0x202f);

/** chiffres groupés par trois : « 1 313 370 969 » (\, en LaTeX) */
function group(digits: string, sep: string): string {
  if (digits.length <= 4) return digits;
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}
function gcd(a: bigint, b: bigint): bigint {
  a = abs(a);
  b = abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1n;
}

export function q(n: bigint | number, d: bigint | number = 1n): Q {
  let N = BigInt(n);
  let D = BigInt(d);
  if (D === 0n) throw new Error('division par zéro');
  if (D < 0n) {
    N = -N;
    D = -D;
  }
  const g = gcd(N, D);
  return { n: N / g, d: D / g };
}

export const ZERO = q(0);
export const ONE = q(1);
export const add = (a: Q, b: Q) => q(a.n * b.d + b.n * a.d, a.d * b.d);
export const sub = (a: Q, b: Q) => q(a.n * b.d - b.n * a.d, a.d * b.d);
export const mul = (a: Q, b: Q) => q(a.n * b.n, a.d * b.d);
export const div = (a: Q, b: Q) => q(a.n * b.d, a.d * b.n);
export const neg = (a: Q) => q(-a.n, a.d);
export const isZero = (a: Q) => a.n === 0n;
export const eq = (a: Q, b: Q) => a.n === b.n && a.d === b.d;
export const toNumber = (a: Q) => Number(a.n) / Number(a.d);

/** « 3 », « −2,5 », « 0,25 », « 3/4 » (virgule ou point, − ou -, 6 décimales au plus) ; null sinon */
export function parseQ(text: string): Q | null {
  const t = text.trim().replace(/−/g, '-').replace(',', '.');
  const m = /^(-?)(\d+)(?:\.(\d{1,6}))?(?:\/(\d+))?$/.exec(t);
  if (!m) return null;
  const [, sign, int, dec = '', den] = m;
  if (den !== undefined && dec) return null;
  const n = BigInt(int! + dec) * (sign ? -1n : 1n);
  const base = 10n ** BigInt(dec.length);
  if (den !== undefined) {
    const D = BigInt(den);
    if (D === 0n) return null;
    return q(n, D);
  }
  return q(n, base);
}

export interface Shown {
  tex: string;
  plain: string;
  exact: boolean;
}

/** nombre de décimales d'un décimal exact (d = 2^i 5^j), ou null */
function decimals(d: bigint): number | null {
  let rest = d;
  let twos = 0;
  let fives = 0;
  while (rest % 2n === 0n) {
    rest /= 2n;
    twos++;
  }
  while (rest % 5n === 0n) {
    rest /= 5n;
    fives++;
  }
  return rest === 1n ? Math.max(twos, fives) : null;
}

function decimalText(a: Q, k: number): string {
  // a × 10^k est entier
  const scaled = (abs(a.n) * 10n ** BigInt(k)) / a.d;
  const s = scaled.toString().padStart(k + 1, '0');
  const int = s.slice(0, s.length - k);
  const frac = s.slice(s.length - k).replace(/0+$/, '');
  return frac ? `${int},${frac}` : int;
}

/**
 * Écriture d'un rationnel : entier exact (15 chiffres au plus, groupés par
 * trois), décimal exact (6 décimales au plus), fraction lisible (6 chiffres),
 * sinon valeur approchée — en écriture scientifique au-delà du million.
 */
export function show(a: Q): Shown {
  const negative = a.n < 0n;
  const sgnTex = negative ? '-' : '';
  const sgnPlain = negative ? '−' : '';
  if (a.d === 1n && abs(a.n) < 10n ** 15n) {
    const t = abs(a.n).toString();
    return {
      tex: `${sgnTex}${group(t, '\\,')}`,
      plain: `${sgnPlain}${group(t, NNBSP)}`,
      exact: true,
    };
  }
  const k = decimals(a.d);
  if (k !== null && k <= 6 && abs(a.n) / a.d < 10n ** 6n) {
    const t = decimalText(a, k);
    return { tex: `${sgnTex}${t.replace(',', '{,}')}`, plain: `${sgnPlain}${t}`, exact: true };
  }
  if (abs(a.n) < 10n ** 6n && a.d < 10n ** 6n)
    return {
      tex: `${sgnTex}\\frac{${abs(a.n)}}{${a.d}}`,
      plain: `${sgnPlain}${abs(a.n)}/${a.d}`,
      exact: true,
    };
  const v = Math.abs(toNumber(a));
  if (v >= 1e6) {
    const [m, e] = v.toExponential(3).split('e');
    const mant = String(Number(m)).replace('.', ',');
    const exp = Number(e);
    return {
      tex: `${sgnTex}${mant.replace(',', '{,}')} \\times 10^{${exp}}`,
      plain: `${sgnPlain}${mant} × 10${superscriptDigits(exp)}`,
      exact: false,
    };
  }
  const r = Number(v.toFixed(4));
  const t = String(r).replace('.', ',');
  return { tex: `${sgnTex}${t.replace(',', '{,}')}`, plain: `${sgnPlain}${t}`, exact: false };
}

const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';
const superscriptDigits = (n: number) =>
  String(n)
    .split('')
    .map((c) => (c === '-' ? '⁻' : SUP[Number(c)]))
    .join('');

/** atomes de la feuille pour un rationnel exact */
export function atoms(a: Q): string[] {
  const s = show(a);
  if (!s.exact) return [];
  const neg = a.n < 0n;
  if (a.d === 1n) return neg ? ['−', abs(a.n).toString()] : [a.n.toString()];
  const body = s.plain.replace('−', '');
  const parts = body.includes('/') ? [body.split('/')[0]!, '÷', body.split('/')[1]!] : [body];
  return neg ? ['−', ...parts] : parts;
}

/* ------------------------------------------------------------------ */
/* matrices                                                            */
/* ------------------------------------------------------------------ */

export type Mat = Q[][];

/** matrice de nombres décimaux (6 décimales au plus) → matrice exacte */
export const fromNumbers = (rows: number[][]): Mat =>
  rows.map((r) => r.map((v) => q(Math.round(v * 1e6), 1e6)));

export const identity = (n: number): Mat =>
  Array.from({ length: n }, (_, i) => Array.from({ length: n }, (_, j) => (i === j ? ONE : ZERO)));

export function matMul(A: Mat, B: Mat): Mat {
  const n = A.length;
  const m = B[0]!.length;
  const k = B.length;
  return Array.from({ length: n }, (_, i) =>
    Array.from({ length: m }, (_, j) => {
      let s = ZERO;
      for (let t = 0; t < k; t++) s = add(s, mul(A[i]![t]!, B[t]![j]!));
      return s;
    }),
  );
}

export function matPow(A: Mat, n: number): Mat {
  let R = identity(A.length);
  for (let i = 0; i < n; i++) R = matMul(R, A);
  return R;
}

export const det2 = (A: Mat) => sub(mul(A[0]![0]!, A[1]![1]!), mul(A[0]![1]!, A[1]![0]!));

/** inverse d'une matrice 2 × 2 (null si det = 0) */
export function inv2(A: Mat): Mat | null {
  const d = det2(A);
  if (isZero(d)) return null;
  const [[a, b], [c, e]] = A as [[Q, Q], [Q, Q]];
  return [
    [div(e, d), div(neg(b), d)],
    [div(neg(c), d), div(a, d)],
  ];
}

export const matEq = (A: Mat, B: Mat) => A.every((r, i) => r.every((x, j) => eq(x, B[i]![j]!)));

/** \begin{pmatrix}…\end{pmatrix} ; `approx` si une case n'est pas exacte */
export function matTex(A: Mat): { tex: string; exact: boolean } {
  let exact = true;
  const rows = A.map((r) =>
    r
      .map((x) => {
        const s = show(x);
        if (!s.exact) exact = false;
        return s.tex;
      })
      .join(' & '),
  );
  return { tex: `\\begin{pmatrix} ${rows.join(' \\\\ ')} \\end{pmatrix}`, exact };
}

/** facteur d'un produit : « 3 », « (-2) », « 0{,}5 » */
export const factorTex = (x: Q) => (x.n < 0n ? `(${show(x).tex})` : show(x).tex);

/** case (i, j) de AB : ligne i de A fois colonne j de B, écrit en entier */
export function cellTex(A: Mat, B: Mat, i: number, j: number): string {
  const terms = A[i]!.map((a, t) => `${factorTex(a)} \\times ${factorTex(B[t]![j]!)}`);
  const r = matMul(A, B)[i]![j]!;
  const s = show(r);
  return `${terms.join(' + ')} ${s.exact ? '=' : '\\approx'} ${s.tex}`;
}

/**
 * Système (n inconnues) : solution unique par élimination de Gauss, null si
 * la matrice n'est pas inversible.
 */
export function solve(A: Mat, y: Q[]): Q[] | null {
  const n = A.length;
  const M = A.map((r, i) => [...r, y[i]!]);
  for (let c = 0; c < n; c++) {
    const p = M.findIndex((r, i) => i >= c && !isZero(r[c]!));
    if (p < 0) return null;
    [M[c], M[p]] = [M[p]!, M[c]!];
    const piv = M[c]![c]!;
    M[c] = M[c]!.map((x) => div(x, piv));
    for (let i = 0; i < n; i++) {
      if (i === c || isZero(M[i]![c]!)) continue;
      const f = M[i]![c]!;
      M[i] = M[i]!.map((x, j) => sub(x, mul(f, M[c]![j]!)));
    }
  }
  return M.map((r) => r[n]!);
}
